import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, Card } from "@/components/kit";
import { ImageField } from "@/components/PlatformTools";
import { mergeTour, videoSource } from "@/lib/tourContent";

const MEDIA = [["image", "Screenshot"], ["video", "Video"], ["both", "Video + screenshot"]];

function VideoField({ s, i, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Video is too large (max 25 MB). Try a shorter clip or use a YouTube link.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/site-videos", fd);
      onChange({ ...s, video: data.url, media: s.media === "image" || !s.media ? "video" : s.media });
      toast.success("Video uploaded. Publish to make it live.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const uploaded = s.video?.startsWith("/api");
  const bad = s.video && !videoSource(s.video);
  return (
    <div className="grid gap-2 rounded-xl bg-slate-50 p-3">
      <div className="flex flex-wrap gap-1.5">{MEDIA.map(([k, l]) => <button type="button" key={k} data-testid={`tour-media-${k}-${i}`} onClick={() => onChange({ ...s, media: k })} className={`ka-press rounded-full px-3 py-1 text-xs font-semibold ${(s.media || "image") === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>)}</div>
      {(s.media || "image") !== "image" && <>
        <Field label="Video" hint={uploaded ? "Your uploaded clip is used. Clear it to use a link or a different clip." : "Paste a YouTube or Vimeo link, or upload an MP4/WEBM clip (max 25 MB)"}>
          <div className="flex flex-wrap gap-2">
            <input data-testid={`tour-video-url-${i}`} className="ka-input min-w-[200px] flex-1" placeholder="https://www.youtube.com/watch?v=…" value={uploaded ? "Uploaded clip" : s.video || ""} readOnly={uploaded} onChange={(e) => onChange({ ...s, video: e.target.value.trim() })} />
            <label data-testid={`tour-video-upload-${i}`} className="ka-press inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold hover:border-slate-900">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />}{busy ? "Uploading…" : "Upload clip"}<input data-testid={`tour-video-input-${i}`} type="file" accept="video/mp4,video/webm" className="hidden" disabled={busy} onChange={pick} /></label>
            {s.video && <button type="button" data-testid={`tour-video-clear-${i}`} onClick={() => onChange({ ...s, video: "" })} className="text-xs font-semibold text-slate-500 hover:text-rose-600">Clear</button>}
          </div>
        </Field>
        {bad && <p data-testid={`tour-video-error-${i}`} className="text-xs text-rose-600">That link isn't a YouTube or Vimeo video.</p>}
        {!s.video && <p className="text-xs text-amber-700">No video yet, so the screenshot shows instead.</p>}
      </>}
    </div>
  );
}

const BLANK = { eyebrow: "", title: "New section", text: "", image: "", video: "", media: "image" };

function SectionEditor({ s, i, n, onChange, onMove, onRemove }) {
  const set = (k) => (e) => onChange({ ...s, [k]: e.target.value });
  return (
    <div data-testid={`tour-edit-section-${i}`} className="grid gap-4 rounded-2xl border border-slate-200 p-4 lg:grid-cols-[280px_1fr]">
      <ImageField label={`Screenshot ${i + 1}`} testid={`tour-image-${i}`} value={s.image} fallback="/icon-512.png" onChange={(v) => onChange({ ...s, image: v })} />
      <div className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
          <Field label="Small label"><input data-testid={`tour-eyebrow-${i}`} maxLength={60} className="ka-input" value={s.eyebrow} onChange={set("eyebrow")} /></Field>
          <Field label="Section title *"><input data-testid={`tour-title-${i}`} required maxLength={80} className="ka-input" value={s.title} onChange={set("title")} /></Field>
        </div>
        <Field label="Description"><textarea data-testid={`tour-text-${i}`} rows={4} maxLength={800} className="ka-input" value={s.text} onChange={set("text")} /></Field>
        <VideoField s={s} i={i} onChange={onChange} />
        <div className="flex gap-2">
          <button type="button" data-testid={`tour-up-${i}`} disabled={i === 0} onClick={() => onMove(-1)} className="ka-press rounded-full border border-slate-300 p-2 disabled:opacity-40"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button type="button" data-testid={`tour-down-${i}`} disabled={i === n - 1} onClick={() => onMove(1)} className="ka-press rounded-full border border-slate-300 p-2 disabled:opacity-40"><ArrowDown className="h-3.5 w-3.5" /></button>
          <button type="button" data-testid={`tour-remove-${i}`} onClick={onRemove} className="ka-press ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" />Remove</button>
        </div>
      </div>
    </div>
  );
}

export function TourEditor() {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/public/tour").then((r) => setT(mergeTour(r.data)));
  }, []);
  if (!t) return null;
  const setSec = (sections) => setT({ ...t, sections });
  const move = (i, d) => { const s = [...t.sections]; [s[i], s[i + d]] = [s[i + d], s[i]]; setSec(s); };
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const save = (e) => { e.preventDefault(); run(async () => setT(mergeTour((await api.put("/platform/tour", t)).data)), "Product tour published"); };
  const reset = () => window.confirm("Go back to the original tour text and screenshots?") && run(async () => { await api.delete("/platform/tour"); setT(mergeTour({})); }, "Tour reset to the original");
  return (
    <Card>
      <form onSubmit={save} className="grid gap-4">
        <p className="text-sm text-slate-500">This is the public page at /tour, linked from the website menu. Upload your own screenshot for any section, reword it, reorder or add sections.</p>
        <Field label="Page title"><input data-testid="tour-page-title" maxLength={120} className="ka-input" value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} /></Field>
        <Field label="Introduction"><textarea data-testid="tour-page-intro" rows={2} maxLength={500} className="ka-input" value={t.intro} onChange={(e) => setT({ ...t, intro: e.target.value })} /></Field>
        {t.sections.map((s, i) => <SectionEditor key={i} s={s} i={i} n={t.sections.length} onChange={(v) => setSec(t.sections.map((x, j) => (j === i ? v : x)))} onMove={(d) => move(i, d)} onRemove={() => setSec(t.sections.filter((_, j) => j !== i))} />)}
        <button type="button" data-testid="tour-add-section" disabled={t.sections.length >= 30} onClick={() => setSec([...t.sections, BLANK])} className="ka-press inline-flex items-center gap-1.5 justify-self-start rounded-full border border-dashed border-slate-400 px-4 py-2 text-sm font-semibold hover:border-slate-900"><Plus className="h-4 w-4" />Add section</button>
        <div className="flex flex-wrap gap-2">
          <button data-testid="tour-save-button" disabled={busy} className="ka-press rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Publish tour"}</button>
          <a href="/tour" target="_blank" rel="noreferrer" data-testid="tour-preview-link" className="ka-press rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold hover:border-slate-900">View page</a>
          <button type="button" data-testid="tour-reset-button" disabled={busy} onClick={reset} className="ka-press rounded-full px-4 py-3 text-sm font-semibold text-slate-500 hover:text-rose-600">Reset to original</button>
        </div>
      </form>
    </Card>
  );
}
